import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import MuiLink from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import {
  getOperatingHours,
  getCancellationPolicy,
  listCourts,
  type CancellationPolicyResponse,
  type Court,
  type FacilityOperatingHoursSnapshot,
} from "@/libs/facilityApi";
import { listPricingRules, type PricingRule } from "@/libs/ratesApi";
import {
  COURT_RESERVATION_SIMPLE_CONFIG,
  SimpleRatesMode,
} from "@/components/rates/simple-rates-mode";

// SetupTab — the configuration half of Court Reservation. Pricing is scoped to
// court-reservation rules; shared courts and facility-wide booking policies are
// summarized here and edited only on their canonical surfaces.

const DASH = "—";

function courtsSummary(courts: Court[] | null): string {
  if (!courts) return DASH;
  const total = courts.length;
  if (total === 0) return "No courts yet";
  const enabled = courts.filter((court) => court.status === "enabled").length;
  const disabled = courts.filter((court) => court.status === "disabled").length;
  const maintenance = courts.filter(
    (court) => court.status === "maintenance",
  ).length;
  const parts = [
    `${total} court${total === 1 ? "" : "s"}`,
    `${enabled} enabled`,
  ];
  if (disabled > 0) parts.push(`${disabled} disabled`);
  if (maintenance > 0) parts.push(`${maintenance} in maintenance`);
  return parts.join(" · ");
}

function operatingHoursSummary(
  snapshot: FacilityOperatingHoursSnapshot | null,
): string {
  if (!snapshot) return DASH;
  if (snapshot.schedules.length === 0) return "Not set";
  return `${snapshot.schedules.length} effective schedule${snapshot.schedules.length === 1 ? "" : "s"}`;
}

function cancellationSummary(
  policy: CancellationPolicyResponse | null,
): string {
  if (!policy) return DASH;
  const count = policy.windows.length;
  if (count === 0) return "No cancellation windows";
  return `${count} cancellation window${count === 1 ? "" : "s"}`;
}

export interface SetupTabProps {
  facilityId: string;
}

export function SetupTab({ facilityId }: SetupTabProps) {
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState<PricingRule[]>([]);
  const [courts, setCourts] = useState<Court[] | null>(null);
  const [operatingHours, setOperatingHours] =
    useState<FacilityOperatingHoursSnapshot | null>(null);
  const [cancellationPolicy, setCancellationPolicy] =
    useState<CancellationPolicyResponse | null>(null);

  const courtRules = rules.filter(
    (rule) => rule.service === "court_reservation",
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const [ruleResult, courtResult, hoursResult, policyResult] =
        await Promise.allSettled([
          listPricingRules(facilityId),
          listCourts(facilityId),
          getOperatingHours(facilityId),
          getCancellationPolicy(facilityId),
        ]);
      if (cancelled) return;
      setRules(ruleResult.status === "fulfilled" ? ruleResult.value : []);
      setCourts(courtResult.status === "fulfilled" ? courtResult.value : null);
      setOperatingHours(
        hoursResult.status === "fulfilled" ? hoursResult.value : null,
      );
      setCancellationPolicy(
        policyResult.status === "fulfilled" ? policyResult.value : null,
      );
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [facilityId]);

  const refreshRules = useCallback(async () => {
    setRules(await listPricingRules(facilityId));
  }, [facilityId]);

  if (loading) {
    return (
      <Stack
        aria-label="Loading reservation setup"
        role="status"
        spacing={1}
        sx={{
          alignItems: "center",
          justifyContent: "center",
          minHeight: "30vh",
        }}
      >
        <CircularProgress aria-hidden size={24} />
        <Typography color="text.secondary" variant="body2">
          Loading…
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: { xs: 2, sm: 2.5 } }} variant="outlined">
        <Stack spacing={2}>
          <Typography component="h2" variant="h6">
            Court reservation pricing
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Set the hourly price players pay to reserve a court. Add peak,
            weekend, or member rates as needed.
          </Typography>
          <SimpleRatesMode
            config={COURT_RESERVATION_SIMPLE_CONFIG}
            currency="USD"
            facilityId={facilityId}
            onSaved={refreshRules}
            rules={courtRules}
          />
          <Typography color="text.secondary" variant="caption">
            Need custom time windows, recurring rules, or per-plan overrides?{" "}
            <MuiLink component={RouterLink} to="/settings/rates">
              Open the full rates editor
            </MuiLink>
            .
          </Typography>
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 2.5 } }} variant="outlined">
        <Stack spacing={2}>
          <Typography component="h2" variant="h6">
            Courts
          </Typography>
          <Typography color="text.secondary" variant="body2">
            The courts players can reserve. Courts are shared across drop-in,
            classes, and leagues — manage them once here.
          </Typography>
          <Typography sx={{ fontWeight: 700 }} variant="body2">
            {courtsSummary(courts)}
          </Typography>
          <Stack direction="row">
            <Button
              component={RouterLink}
              size="small"
              to="/courts"
              variant="outlined"
            >
              Manage courts
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 2.5 } }} variant="outlined">
        <Stack spacing={2}>
          <Typography component="h2" variant="h6">
            Booking policies
          </Typography>
          <Typography color="text.secondary" variant="body2">
            When courts are bookable and what happens when a player cancels.
          </Typography>
          <Stack component="dl" spacing={1} sx={{ m: 0 }}>
            <Stack
              component="div"
              direction="row"
              spacing={2}
              sx={{ alignItems: "center", justifyContent: "space-between" }}
            >
              <Typography color="text.secondary" component="dt" variant="body2">
                Operating hours
              </Typography>
              <Typography
                component="dd"
                sx={{ fontWeight: 700, m: 0 }}
                variant="body2"
              >
                {operatingHoursSummary(operatingHours)}
              </Typography>
            </Stack>
            <Stack
              component="div"
              direction="row"
              spacing={2}
              sx={{ alignItems: "center", justifyContent: "space-between" }}
            >
              <Typography color="text.secondary" component="dt" variant="body2">
                Cancellation policy
              </Typography>
              <Typography
                component="dd"
                sx={{ fontWeight: 700, m: 0 }}
                variant="body2"
              >
                {cancellationSummary(cancellationPolicy)}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row">
            <Button
              component={RouterLink}
              size="small"
              to="/settings/facility"
              variant="outlined"
            >
              Edit booking policies
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}
